import React, { useContext, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';

import { check } from '@/assets/icons';
import { NewMessage } from '@/components/NewMessage';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { SetlistPermissionSheet } from '@/components/shared/SetlistPermissionSheet';
import { TeamMilestones, UserMilestones } from '@/config/constants';
import { Permissions } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';

import dashboardStyles from '../../Dashboard.module.scss';
import styles from './GettingStarted.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

export const GettingStarted: React.FC = () => {
	const { hasMilestones } = useTeam();
	const { teamSongs } = useTeamSongs();
	const { setlists, dataUpdatedAt } = useSetlists();
	const { openQuickAddSongs, openSetlistGenerate } = useLayout();
	const { hasPermission } = usePermissions();
	const [permissionSheetOpen, setPermissionSheetOpen] = useState(false);
	const [newMessageOpen, setNewMessageOpen] = useState(false);

	const fireOnbardingCompletedEvent = useAnalyticsEvent(
		'GettingStarted_Onboarding_Completed',
	);

	const addedInitialSongs = hasMilestones([
		TeamMilestones.ADDED_INITIAL_SONGS,
	]);

	const oldestSetlist = useMemo(() => {
		if (setlists.length === 0) {
			return null;
		}
		return setlists.reduce((oldest, current) => {
			return new Date(oldest.startAt) < new Date(current.startAt)
				? oldest
				: current;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setlists, dataUpdatedAt]);

	return (
		<>
			<div className={styles['root']}>
				<div className={dashboardStyles['title']}>Getting started</div>
				<div className={styles['items']}>
					<GettingStartedItem
						number={1}
						buttonComponent={
							<RoundedButton
								className="flashy"
								onClick={openQuickAddSongs}
							>
								Add songs
							</RoundedButton>
						}
						title="Add 10 songs"
						subtitle={
							teamSongs.length
								? `${
										addedInitialSongs
											? 'Nice one!'
											: 'Keep going!'
								  } You've added ${teamSongs.length} so far.`
								: 'So you can create a setlist'
						}
						isCompleted={addedInitialSongs}
						isActive={!addedInitialSongs}
					/>

					<GettingStartedItem
						isLast
						number={2}
						buttonComponent={
							<RoundedButton
								className="flashy"
								onClick={() => {
									if (
										!hasPermission(Permissions.TeamUpdate)
									) {
										setPermissionSheetOpen(true);
										return;
									}
									openSetlistGenerate({
										fromOnboarding: true,
									});
								}}
							>
								Create setlist
							</RoundedButton>
						}
						title="Create a setlist"
						subtitle="Generate with AI or craft yourself"
						isCompleted={hasMilestones([
							TeamMilestones.CREATED_FIRST_SETLIST,
						])}
						isActive={addedInitialSongs}
					/>
				</div>
			</div>
			<SetlistPermissionSheet
				open={permissionSheetOpen}
				onClose={() => setPermissionSheetOpen(false)}
				onNewMessage={() => {
					setPermissionSheetOpen(false);
					setNewMessageOpen(true);
				}}
			/>
			<NewMessage
				open={newMessageOpen}
				handleClose={() => setNewMessageOpen(false)}
				initialMessage
			/>
		</>
	);
};

const GettingStartedItem: React.FC<{
	number: number;
	isLast?: boolean;
	title: string;
	subtitle: string;
	isCompleted?: boolean;
	isActive?: boolean;
	buttonComponent: React.ReactNode;
}> = ({
	number,
	title,
	subtitle,
	isCompleted,
	isActive,
	buttonComponent,
	isLast,
}) => {
	return (
		<div
			className={classNames([
				styles['item'],
				isCompleted ? styles['completed'] : '',
				isActive ? styles['active'] : '',
				isLast ? styles['last'] : '',
			])}
		>
			<div className={styles['timeline']}>
				<div className={styles['number']}>
					{isCompleted ? check({}) : number}
				</div>
				{!isLast ? <div className={styles['line']} /> : null}
			</div>
			<div className={styles['content']}>
				<div className={styles['title']}>{title}</div>
				<div className={styles['subtitle']}>{subtitle}</div>
			</div>
			<div className={styles['actions']}>
				{isActive && !isCompleted ? buttonComponent : null}
			</div>
		</div>
	);
};
